import React from "react";
import {
  Fish,
  ListFilter,
  MessageSquareText,
  Ruler,
  Sailboat,
  Scale,
  ShieldCheck,
  Waves,
} from "lucide-react";

import CreatePostSection from "../CreatePostSection";
import GearIcon from "../GearIcon";
import GearInput from "../GearInput";
import { WAY_OF_FISHING_OPTIONS } from "../../../utils/catchMetadataOptions";

const CATCH_WATER_TYPE_OPTIONS = [
  {
    value: "all",
    labelKey: "all",
    defaultLabel: "All",
  },
  {
    value: "freshwater",
    labelKey: "catch_water_type_freshwater",
    defaultLabel: "Freshwater",
  },
  {
    value: "saltwater",
    labelKey: "catch_water_type_saltwater_marine",
    defaultLabel: "Salt water (marine)",
  },
];

function GearSectionIcon() {
  return (
    <GearIcon
      type="tackleBox"
      className="create-post-gear-section-heading-icon"
    />
  );
}

export default function CatchFormDetailsSection({ catchForm, icon }) {
  const renderFieldLabel = (Icon, label) => (
    <span className="catch-details-label-content">
      <Icon size={14} strokeWidth={2.25} aria-hidden="true" />
      <span>{label}</span>
    </span>
  );

  return (
    <>
      <CreatePostSection
        title={catchForm.t("create_post_section_catch_details")}
        className="create-post-section-card--catch-details"
        icon={icon}
      >
        <div
          className="catch-form-field catch-details-field catch-details-field--species"
          style={{ position: "relative" }}
        >
          <label>
            {renderFieldLabel(Fish, `${catchForm.t("catch_fish_species")}:`)}
          </label>
          <input
            type="text"
            placeholder={catchForm.t("catch_fish_species_placeholder")}
            value={catchForm.fishInput}
            onChange={catchForm.handleFishInputChange}
            aria-invalid={catchForm.hasFishSpeciesError ? "true" : undefined}
            aria-describedby={
              catchForm.hasFishSpeciesError
                ? "catch-fish-species-validation"
                : undefined
            }
          />
          {catchForm.hasFishSpeciesError && (
            <div
              id="catch-fish-species-validation"
              className="catch-form-validation-error"
              role="alert"
            >
              {catchForm.t(catchForm.fishSpeciesErrorKey, {
                defaultValue:
                  "Please enter the fish name using English/Latin letters.",
              })}
            </div>
          )}
          {catchForm.fishSuggestions.length > 0 && (
            <ul className="suggestion-list">
              {catchForm.fishSuggestions.map((fish, index) => (
                <li
                  key={index}
                  onClick={() => catchForm.handleSelectFishSuggestion(fish)}
                >
                  {fish.commonName}{" "}
                  <span className="catch-form-suggestion-subtitle">
                    ({fish.scientificName})
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="catch-details-measure-grid">
          <div className="catch-form-field catch-details-field catch-details-field--measure">
            <label>{renderFieldLabel(Ruler, `${catchForm.t("catch_size")}:`)}</label>
            <div className="unit-input-wrapper">
              {catchForm.sizeUnit === "cm" ? (
                <input
                  type="text"
                  className="unit-input-main"
                  value={catchForm.size}
                  onChange={(e) => catchForm.setSize(e.target.value)}
                  placeholder={catchForm.t("catch_size_placeholder")}
                />
              ) : (
                <div className="imperial-size-row unit-input-main">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={catchForm.sizeFeet}
                    onChange={(e) => catchForm.setSizeFeet(e.target.value)}
                    placeholder="ft"
                  />
                  <span className="imperial-size-separator" aria-hidden="true">
                    /
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={catchForm.sizeInches}
                    onChange={(e) => catchForm.setSizeInches(e.target.value)}
                    placeholder="in"
                  />
                </div>
              )}
              <div
                className="unit-toggle"
                role="group"
                aria-label={catchForm.t("catch_size_units")}
              >
                <button
                  type="button"
                  className={`unit-toggle-btn ${catchForm.sizeUnit === "cm" ? "active" : ""}`}
                  onClick={() => catchForm.setSizeUnit("cm")}
                >
                  cm
                </button>
                <button
                  type="button"
                  className={`unit-toggle-btn ${catchForm.sizeUnit === "ft-in" ? "active" : ""}`}
                  onClick={() => catchForm.setSizeUnit("ft-in")}
                >
                  ft/in
                </button>
              </div>
            </div>
          </div>

          <div className="catch-form-field catch-details-field catch-details-field--measure">
            <label>
              {renderFieldLabel(Scale, `${catchForm.t("catch_weight")}:`)}
            </label>
            <div className="unit-input-wrapper">
              <input
                type="text"
                className="unit-input-main"
                inputMode={catchForm.weightUnit === "lb" ? "decimal" : undefined}
                value={
                  catchForm.weightUnit === "kg"
                    ? catchForm.weight
                    : catchForm.weightLb
                }
                onChange={(e) =>
                  catchForm.weightUnit === "kg"
                    ? catchForm.setWeight(e.target.value)
                    : catchForm.setWeightLb(e.target.value)
                }
                placeholder={
                  catchForm.weightUnit === "kg"
                    ? catchForm.t("catch_weight_placeholder")
                    : catchForm.t("catch_weight_lb_placeholder", {
                        defaultValue: "e.g., 6.6 lb",
                      })
                }
              />
              <div
                className="unit-toggle"
                role="group"
                aria-label={catchForm.t("catch_weight_units")}
              >
                <button
                  type="button"
                  className={`unit-toggle-btn ${catchForm.weightUnit === "kg" ? "active" : ""}`}
                  onClick={() => catchForm.setWeightUnit("kg")}
                >
                  kg
                </button>
                <button
                  type="button"
                  className={`unit-toggle-btn ${catchForm.weightUnit === "lb" ? "active" : ""}`}
                  onClick={() => catchForm.setWeightUnit("lb")}
                >
                  lb
                </button>
              </div>
            </div>
          </div>
        </div>

        {!catchForm.isSimpleModeActive && (
          <div className="catch-details-select-grid">
            <div className="catch-form-field catch-details-field catch-details-field--select">
              <label>
                {renderFieldLabel(
                  Waves,
                  `${catchForm.t("catch_water_type", {
                    defaultValue: "Water type",
                  })}:`
                )}
              </label>
              <select
                value={catchForm.waterType}
                onChange={(e) => catchForm.setWaterType(e.target.value)}
              >
                {CATCH_WATER_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {catchForm.t(option.labelKey, {
                      defaultValue: option.defaultLabel,
                    })}
                  </option>
                ))}
              </select>
            </div>

            <div className="catch-form-field catch-details-field catch-details-field--select">
              <label>
                {renderFieldLabel(
                  Sailboat,
                  `${catchForm.t("catch_way_of_fishing")}:`
                )}
              </label>
              <select
                value={catchForm.wayOfFishing}
                onChange={catchForm.handleWayChange}
              >
                <option value="">{catchForm.t("select")}</option>
                {WAY_OF_FISHING_OPTIONS.map((way) => (
                  <option key={way} value={way}>
                    {way === "Shore"
                      ? catchForm.t("fishing_way_shore", { defaultValue: way })
                      : way === "Boat"
                        ? catchForm.t("fishing_way_boat", {
                            defaultValue: way,
                          })
                        : way}
                  </option>
                ))}
              </select>
            </div>

            <div className="catch-form-field catch-details-field catch-details-field--select">
              <label>
                {renderFieldLabel(
                  ListFilter,
                  `${catchForm.t("catch_type_of_fishing")}:`
                )}
              </label>
              <select
                value={catchForm.typeOfFishing}
                onChange={(e) => catchForm.setTypeOfFishing(e.target.value)}
                className={
                  catchForm.wayOfFishing ? "select-active" : "select-disabled"
                }
                disabled={catchForm.typeOfFishingOptions.length === 0}
              >
                <option value="">{catchForm.t("select")}</option>
                {catchForm.typeOfFishingOptions.map((type, index) => (
                  <option key={index} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="catch-form-field catch-details-field catch-details-field--select">
              <label>
                {renderFieldLabel(
                  ListFilter,
                  `${catchForm.t("catch_style")}:`
                )}
              </label>
              <select
                value={catchForm.style}
                onChange={(e) => catchForm.setStyle(e.target.value)}
                className={
                  catchForm.typeOfFishing ? "select-active" : "select-disabled"
                }
                disabled={catchForm.styleOptions.length === 0}
              >
                <option value="">{catchForm.t("select")}</option>
                {catchForm.styleOptions.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
                {catchForm.styleOptions.length > 0 && (
                  <option value="Other">{catchForm.t("other")}</option>
                )}
              </select>
            </div>

            {catchForm.techniqueOptions.length > 0 && (
              <div className="catch-form-field catch-details-field catch-details-field--select">
                <label>
                  {renderFieldLabel(
                    ListFilter,
                    `${catchForm.t("catch_technique")}:`
                  )}
                </label>
                <select
                  value={catchForm.technique}
                  onChange={(e) => catchForm.setTechnique(e.target.value)}
                  className="select-active"
                >
                  <option value="">{catchForm.t("select")}</option>
                  {catchForm.techniqueOptions.map((technique, index) => (
                    <option key={index} value={technique}>
                      {technique}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </CreatePostSection>
    </>
  );
}

export function CatchFormDetailsAside({ catchForm }) {
  const renderGearLabel = (iconType, label) => (
    <span className="catch-details-label-content gear-field-label-content">
      <GearIcon type={iconType} />
      <span>{label}</span>
    </span>
  );

  return (
    <div
      className={`catch-form-right${catchForm.isSimpleModeActive ? " catch-form-right--simple" : ""}`}
    >
      {!catchForm.isSimpleModeActive && (
        <CreatePostSection
          title={catchForm.t("create_post_section_gear_rig")}
          icon={GearSectionIcon}
          className="create-post-section-card--gear-main"
        >
          {catchForm.isLoadingSavedGearSets ||
          catchForm.savedGearSetLoadError ||
          catchForm.savedGearSets.length > 0 ? (
            <div className="catch-form-field">
              <label>
                {renderGearLabel(
                  "gearSet",
                  `${catchForm.t("catch_saved_gear_label")}:`
                )}
              </label>
              <select
                value={catchForm.savedGearSetId}
                onChange={catchForm.handleSavedGearSetChange}
                disabled={
                  catchForm.isLoadingSavedGearSets ||
                  Boolean(catchForm.savedGearSetLoadError) ||
                  catchForm.savedGearSets.length === 0
                }
              >
                <option value="">
                  {catchForm.isLoadingSavedGearSets
                    ? catchForm.t("loading")
                    : catchForm.savedGearSets.length > 0
                      ? catchForm.t("catch_saved_gear_placeholder")
                      : catchForm.t("catch_saved_gear_none")}
                </option>
                {catchForm.savedGearSets.map((gearSet) => (
                  <option key={gearSet.id} value={gearSet.id}>
                    {gearSet.name}
                  </option>
                ))}
              </select>
              {catchForm.savedGearSetLoadError ? (
                <div className="catch-form-validation-error" role="alert">
                  {catchForm.t(catchForm.savedGearSetLoadError)}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="catch-form-field" style={{ position: "relative" }}>
            <label>{renderGearLabel("rod", `${catchForm.t("rod")}:`)}</label>
            <input
              type="text"
              placeholder={catchForm.t("catch_enter_rod")}
              value={catchForm.rod}
              onFocus={() => catchForm.setActiveDropdown("Rod")}
              onChange={(e) => catchForm.setRod(e.target.value)}
            />

            {catchForm.activeDropdown === "Rod" &&
              catchForm.rod &&
              catchForm.rodSuggestions.length > 0 && (
                <ul className="suggestion-list">
                  {catchForm.rodSuggestions.map((suggestion, index) => (
                    <li
                      key={`${suggestion.value}-${index}`}
                      onClick={() => {
                        catchForm.setRod(suggestion.value);
                        catchForm.setActiveDropdown(null);
                      }}
                    >
                      <div>{suggestion.displayName}</div>
                      {suggestion.displaySubtitle ? (
                        <div className="catch-form-suggestion-subtitle catch-form-suggestion-subtitle--block">
                          {suggestion.displaySubtitle}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
          </div>

          <div className="catch-form-field" style={{ position: "relative" }}>
            <label>{renderGearLabel("reel", `${catchForm.t("reel")}:`)}</label>
            <input
              type="text"
              placeholder={catchForm.t("catch_enter_reel")}
              value={catchForm.reel}
              onFocus={() => catchForm.setActiveDropdown("Reel")}
              onChange={(e) => catchForm.setReel(e.target.value)}
            />

            {catchForm.activeDropdown === "Reel" &&
              catchForm.reel &&
              catchForm.reelSuggestions.length > 0 && (
                <ul className="suggestion-list">
                  {catchForm.reelSuggestions.map((suggestion, index) => (
                    <li
                      key={`${suggestion.value}-${index}`}
                      onClick={() => {
                        catchForm.setReel(suggestion.value);
                        catchForm.setActiveDropdown(null);
                      }}
                    >
                      {suggestion.value}
                    </li>
                  ))}
                </ul>
              )}
          </div>

          <GearInput
            rodDatabase={catchForm.rodDatabase}
            reelDatabase={catchForm.reelDatabase}
            braidDatabase={catchForm.braidDatabase}
            leaderDatabase={catchForm.leaderDatabase}
            lureDatabase={catchForm.lureDatabase}
            hookBrandDatabase={catchForm.hookBrandDatabase}
            baitDatabase={catchForm.baitDatabase}
            liveBaitDatabase={catchForm.liveBaitDatabase}
            deadBaitDatabase={catchForm.deadBaitDatabase}
            combinedHookOptions={catchForm.combinedHookOptions}
            lureSizeOptions={catchForm.lureSizeOptions}
            typeOfFishing={catchForm.typeOfFishing}
            isLureFishing={catchForm.isLureFishingType}
            rod={catchForm.rod}
            setRod={catchForm.setRod}
            reel={catchForm.reel}
            setReel={catchForm.setReel}
            braid={catchForm.braid}
            setBraid={catchForm.setBraid}
            leader={catchForm.leader}
            setLeader={catchForm.setLeader}
            lure={catchForm.lure}
            setLure={catchForm.setLure}
            lureSize={catchForm.lureSize}
            setLureSize={catchForm.setLureSize}
            lureColor={catchForm.lureColor}
            setLureColor={catchForm.setLureColor}
            hookBrand={catchForm.hookBrand}
            setHookBrand={catchForm.setHookBrand}
            hookSpec={catchForm.hookSpec}
            setHookSpec={catchForm.setHookSpec}
            knotBraidToLeader={catchForm.knotBraidToLeader}
            setKnotBraidToLeader={catchForm.setKnotBraidToLeader}
            knotLeaderToLure={catchForm.knotLeaderToLure}
            setKnotLeaderToLure={catchForm.setKnotLeaderToLure}
          />
        </CreatePostSection>
      )}

      <CreatePostSection
        title={catchForm.t("create_post_section_responsible")}
        icon={ShieldCheck}
      >
        {!catchForm.isSimpleModeActive && (
          <div className="catch-form-field">
            <label>{catchForm.t("catch_and_release")}:</label>
            <select
              value={catchForm.catchAndRelease}
              onChange={(e) => catchForm.setCatchAndRelease(e.target.value)}
            >
              <option value="">{catchForm.t("select")}</option>
              <option value="Yes">{catchForm.t("yes")}</option>
              <option value="No">{catchForm.t("no")}</option>
            </select>
          </div>
        )}

        <div className="catch-form-rating-field">
          <label>{catchForm.t("catch_rate_location")}:</label>
          <div className="catch-form-rating-stars">
            {[1, 2, 3, 4, 5].map((value) => (
              <span
                key={value}
                onClick={() => catchForm.handleStarClick(value)}
                style={{
                  cursor: "pointer",
                  color:
                    value <= catchForm.rating ? "#FFD700" : "var(--rp-muted)",
                  marginRight: "4px",
                }}
              >
                {"\u2605"}
              </span>
            ))}
          </div>
        </div>
      </CreatePostSection>
    </div>
  );
}

export function CatchFormFeedbackAndActions({ catchForm }) {
  const disabledSubmitMessage = catchForm.t("catch_media_required", {
    defaultValue: "Please add a photo or video to post your catch.",
  });
  const blockedMediaMessage = catchForm.mediaError
    ? catchForm.t("catch_media_submit_blocked_hint", {
        defaultValue:
          "Photo upload is blocked because the selected image could not be prepared. Please reselect the photo before posting.",
      })
    : disabledSubmitMessage;
  const isSubmitDisabled =
    catchForm.isMissingMedia ||
    catchForm.isSubmitting ||
    catchForm.hasFishSpeciesError;
  const submitButtonLabel = catchForm.isSubmitting
    ? catchForm.t("create_post_uploading", {
        defaultValue: "Uploading...",
      })
    : catchForm.t("Post");
  const submitStatusTone = catchForm.submitStatusTone;

  return (
    <>
      {catchForm.showFeedback && !catchForm.isSimpleModeActive && (
        <CreatePostSection
          title={catchForm.t("create_post_section_feedback")}
          className="create-post-section-card--feedback"
          icon={MessageSquareText}
        >
          <label className="catch-form-feedback-label">
            {catchForm.t("catch_feedback")}:
          </label>
          <textarea
            placeholder={catchForm.t("catch_feedback_placeholder")}
            value={catchForm.feedback}
            onChange={(e) => catchForm.setFeedback(e.target.value)}
            className="catch-form-feedback-textarea"
          />
        </CreatePostSection>
      )}

      <div className="create-post-form-actions">
        <button
          onClick={catchForm.handleCancel}
          disabled={catchForm.isSubmitting}
          className="create-post-cancel-btn"
        >
          {catchForm.t("cancel")}
        </button>

        <div
          className={`catch-submit-tooltip-wrapper${catchForm.isMissingMedia ? " is-disabled" : ""}`}
          title={catchForm.isMissingMedia ? blockedMediaMessage : undefined}
        >
          {catchForm.isMissingMedia && (
            <div className="catch-submit-tooltip" role="tooltip">
              {blockedMediaMessage}
            </div>
          )}

          <span className="catch-submit-tooltip-trigger">
            <button
              onClick={catchForm.handlePostSubmit}
              disabled={isSubmitDisabled}
              className={`create-post-submit-btn${isSubmitDisabled ? " is-disabled" : ""}${catchForm.isSubmitting ? " is-loading" : ""}`}
              aria-busy={catchForm.isSubmitting}
            >
              {catchForm.isSubmitting && (
                <span
                  className="create-post-submit-spinner"
                  aria-hidden="true"
                />
              )}
              {submitButtonLabel}
            </button>
          </span>
        </div>
      </div>
      {catchForm.isMissingMedia && catchForm.mediaError && (
        <div className="catch-form-submit-message catch-form-submit-message--error">
          {blockedMediaMessage}
        </div>
      )}
      {catchForm.submitStatusMessage && (
        <div
          className={`catch-form-submit-message catch-form-submit-message--${submitStatusTone === "error" ? "error" : "info"}`}
        >
          {catchForm.submitStatusMessage}
        </div>
      )}
    </>
  );
}
